import React, { useState, useEffect } from 'react';
import RecipeModalGateway from './RecipeModalGateway';
import RecipeMenuGroup from './RecipeMenuGroup';
import Swal from 'sweetalert2';
import useMenuApproval from '../hooks/useMenuApproval';
import { API_BASE_URL } from '../config';

export default function SmartSearchModal({
    isOpen,
    onClose,
    loading,
    recipes,
    structuredMenu,
    onRecipeClick,
    isDarkMode,
    selectedRecipe,
    recipeModalOpen,
    onRecipeModalClose,
    userProfile,
    onSearchUpdate,
    onUsed,
    onRecipeUsedSuccess,
    availableComponents = [],
    initialPortion = 4,
    initialSkillLevel = 'Acemi'
}) {
    const [selectedCategories, setSelectedCategories] = useState(['main', 'soup', 'salad']);
    const [selectedRecipeNames, setSelectedRecipeNames] = useState([]);
    const [viewMode, setViewMode] = useState('smart_menu'); // 'smart_menu' | 'browse_all'
    const [browseRecipes, setBrowseRecipes] = useState([]);
    const [browseLoading, setBrowseLoading] = useState(false);

    // Portion and Skill state
    const [portion, setPortion] = useState(initialPortion);
    const [skillLevel, setSkillLevel] = useState(initialSkillLevel);
    const [preferencesChanged, setPreferencesChanged] = useState(false);

    const { approveMenu, isApproving } = useMenuApproval({
        onSuccess: () => {
            setSelectedRecipeNames([]);
            if (onUsed) onUsed();
            onClose();
        }
    });

    useEffect(() => {
        if (isOpen) {
            setPortion(initialPortion);
            setSkillLevel(initialSkillLevel);
            setPreferencesChanged(false);
            setSelectedRecipeNames([]);
        }
    }, [isOpen, initialPortion, initialSkillLevel]);

    const toggleCategory = (catId) => {
        const newCats = selectedCategories.includes(catId)
            ? selectedCategories.filter(c => c !== catId)
            : [...selectedCategories, catId];
        setSelectedCategories(newCats);
        setPreferencesChanged(true);
    };

    const toggleSelection = (recipeOrRecipes) => {
        const recipesToToggle = Array.isArray(recipeOrRecipes) ? recipeOrRecipes : [recipeOrRecipes];
        const namesToToggle = recipesToToggle.map(r => r.name || r.title);

        let newSelection = [...selectedRecipeNames];
        const allSelected = namesToToggle.every(name => newSelection.includes(name));

        if (allSelected) {
            newSelection = newSelection.filter(name => !namesToToggle.includes(name));
        } else {
            namesToToggle.forEach(name => {
                if (!newSelection.includes(name)) newSelection.push(name);
            });
        }
        setSelectedRecipeNames(newSelection);
    };

    const handleSearchUpdate = () => {
        if (onSearchUpdate) {
            onSearchUpdate(selectedCategories, { portion: parseInt(portion), skillLevel });
        }
        setPreferencesChanged(false);
    };

    const handleMarkMenuAsUsed = async () => {
        let recipesToProcess = [];
        if (viewMode === 'browse_all') {
            recipesToProcess = browseRecipes.filter(r => selectedRecipeNames.includes(r.name || r.title));
        } else if (structuredMenu) {
            const { main, pair, soup, salad, dessert } = structuredMenu;
            const allStructured = [
                ...(main ? [main] : []),
                ...(pair || []),
                ...(soup ? [soup] : []),
                ...(salad ? [salad] : []),
                ...(dessert ? [dessert] : [])
            ];
            recipesToProcess = allStructured.filter(r => selectedRecipeNames.includes(r.name || r.title));
        } else {
            recipesToProcess = (recipes || []).filter(r => selectedRecipeNames.includes(r.name || r.title));
        }

        if (recipesToProcess.length === 0) {
            Swal.fire({
                title: 'Tarif Seçmediniz',
                text: 'Lütfen pişirmek istediğiniz tarifleri üzerine tıklayarak seçin.',
                icon: 'info',
                confirmButtonText: 'Anladım'
            });
            return;
        }

        approveMenu(recipesToProcess, {
            mealSlot: null,
            currentServingSize: Number(portion || 4) || 4
        });
    };

    const handleBrowseAll = async () => {
        setBrowseLoading(true);
        setViewMode('browse_all');

        try {
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${API_BASE_URL}/api/smart-search/browse-all`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ categories: selectedCategories })
            });

            const data = await response.json();
            if (data.success) {
                setBrowseRecipes(data.recipes || []);
            } else {
                Swal.fire('Hata', data.message || 'Tarifler yüklenemedi', 'error');
            }
        } catch (error) {
            console.error('Browse all error:', error);
            Swal.fire('Hata', 'Tarifler yüklenirken bir sorun oluştu', 'error');
        } finally {
            setBrowseLoading(false);
        }
    };

    if (!isOpen) return null;

    const categories = [
        { id: 'soup', label: 'Çorba', icon: 'soup_kitchen' },
        { id: 'main', label: 'Ana Yemek', icon: 'restaurant' },
        { id: 'salad', label: 'Salata/Meze', icon: 'eco' },
        { id: 'dessert', label: 'Tatlı', icon: 'cake' }
    ];

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-0 md:p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" onClick={onClose} />

            <div className={`relative w-full h-full md:h-auto md:max-h-[92vh] md:max-w-5xl md:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden transition-all duration-300 border-t border-slate-100 dark:border-slate-800 ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-800'}`}>

                {/* Modern Header Section */}
                <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800 z-10 bg-inherit/80 backdrop-blur-md sticky top-0">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="material-symbols-outlined text-red-500 text-[20px] md:text-[24px]">magic_button</span>
                                <h2 className="text-lg md:text-2xl font-black bg-gradient-to-r from-red-500 to-rose-600 bg-clip-text text-transparent">Akıllı Mutfak</h2>
                            </div>
                            <p className="text-[9px] md:text-[11px] font-bold text-slate-400 uppercase tracking-widest">En İyi Eşleşmeye Göre</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleBrowseAll}
                                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-black text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-all flex items-center gap-2 border border-slate-200 dark:border-slate-700 shadow-sm"
                            >
                                <span className="material-symbols-outlined text-[18px]">library_books</span>
                                Tüm Tarifler
                            </button>
                            <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl transition-all active:rotate-90 text-slate-400">
                                <span className="material-symbols-outlined text-[28px]">close</span>
                            </button>
                        </div>
                    </div>

                    {/* Toolbar: Categories + Portion/Skill */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-3 md:p-4 bg-slate-50/50 dark:bg-slate-800/30 rounded-2xl md:rounded-3xl border border-slate-100 dark:border-slate-800/50">
                        {/* Categories */}
                        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-1.5 md:gap-2 w-full lg:flex-1">
                            {categories.map(cat => {
                                const isActive = selectedCategories.includes(cat.id);
                                const activeColors = {
                                    soup: 'bg-amber-500 text-white border-amber-600 shadow-amber-500/20',
                                    main: 'bg-red-500 text-white border-red-600 shadow-red-500/20',
                                    salad: 'bg-emerald-500 text-white border-emerald-600 shadow-emerald-500/20',
                                    dessert: 'bg-purple-500 text-white border-purple-600 shadow-purple-500/20'
                                };

                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => toggleCategory(cat.id)}
                                        className={`flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-xl border-2 transition-all active:scale-95 ${isActive
                                            ? `${activeColors[cat.id]} shadow-lg`
                                            : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-500 hover:border-slate-200 dark:hover:border-slate-700'
                                            }`}
                                    >
                                        <span className="material-symbols-outlined text-[18px] md:text-[20px]">{cat.icon}</span>
                                        <span className="text-[10px] md:text-[11px] font-black uppercase tracking-wider">{cat.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="hidden lg:block w-px h-8 bg-slate-200 dark:bg-slate-700/50 mx-2" />

                        {/* Preferred Config */}
                        <div className="flex items-center justify-between lg:justify-start gap-2 md:gap-3 w-full lg:w-auto mt-1 lg:mt-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-100 dark:border-slate-800/50">
                            {/* Portion */}
                            <div className="relative h-9 md:h-10 flex-1 lg:w-24">
                                <input
                                    type="number"
                                    min={1}
                                    value={portion}
                                    onChange={e => {
                                        setPortion(e.target.value);
                                        setPreferencesChanged(true);
                                    }}
                                    className="w-full h-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl pl-3 md:pl-4 pr-7 md:pr-8 text-xs font-black focus:border-red-500 outline-none transition-all shadow-sm"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] md:text-[10px] text-slate-400 font-bold italic">Kişi</span>
                            </div>

                            {/* Skill */}
                            <div className="flex bg-white dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm h-9 md:h-10 flex-[2] lg:flex-none">
                                {['Acemi', 'Deneyimli', 'Usta Şef'].map((lvl) => (
                                    <button
                                        key={lvl}
                                        onClick={() => {
                                            setSkillLevel(lvl);
                                            setPreferencesChanged(true);
                                        }}
                                        className={`flex-1 px-2 md:px-3 rounded-lg text-[9px] md:text-[10px] font-black transition-all ${skillLevel === lvl
                                            ? 'bg-red-500 text-white shadow-md'
                                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                                            }`}
                                    >
                                        {lvl.split(' ')[0]}
                                    </button>
                                ))}
                            </div>

                            {/* Refresh Button */}
                            <button
                                onClick={handleSearchUpdate}
                                disabled={!preferencesChanged}
                                className={`h-9 md:h-10 w-9 md:w-10 flex items-center justify-center rounded-xl transition-all active:scale-95 shrink-0 ${preferencesChanged
                                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 opacity-50'
                                    }`}
                            >
                                <span className={`material-symbols-outlined text-[18px] md:text-[20px] ${preferencesChanged ? 'animate-spin-slow' : ''}`}>sync</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 pb-40 no-scrollbar">
                    {viewMode === 'browse_all' ? (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <div className="mb-6 flex items-center justify-between sticky top-0 bg-inherit py-2 z-10">
                                <button
                                    onClick={() => setViewMode('smart_menu')}
                                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 font-bold hover:text-red-500 transition-all shadow-sm"
                                >
                                    <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                                    Önerilere Dön
                                </button>
                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{browseRecipes.length} TARİF BULUNDU</p>
                            </div>

                            {browseLoading ? (
                                <div className="flex flex-col items-center justify-center h-80 space-y-6">
                                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                    <p className="font-black animate-pulse text-blue-500">Tüm tarifler getiriliyor...</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {(() => {
                                        const pairedChildIds = new Set();
                                        const pairedChildNames = new Set();

                                        browseRecipes.forEach((r) => {
                                            (r?.suggestedPairings || []).forEach((p) => {
                                                if (p?.id !== undefined && p?.id !== null) pairedChildIds.add(String(p.id));
                                                if (p?.name) pairedChildNames.add(String(p.name).trim().toLocaleLowerCase('tr-TR'));
                                            });
                                        });

                                        const renderedRootIds = new Set();
                                        const renderable = browseRecipes.filter((recipe) => {
                                            if (!recipe) return false;
                                            const idKey = recipe.id !== undefined && recipe.id !== null ? String(recipe.id) : null;
                                            const nameKey = String(recipe.name || recipe.title || '').trim().toLocaleLowerCase('tr-TR');

                                            if (idKey && renderedRootIds.has(idKey)) return false;
                                            if (idKey) renderedRootIds.add(idKey);

                                            const isPairedChild =
                                                (idKey ? pairedChildIds.has(idKey) : false) ||
                                                (!!nameKey && pairedChildNames.has(nameKey));

                                            return !isPairedChild;
                                        });

                                        if (renderable.length === 0) {
                                            return (
                                                <div className="col-span-full py-12 text-center text-slate-500 dark:text-slate-400 font-semibold">
                                                    Tum tariflerde eslesme bulunamadi.
                                                </div>
                                            );
                                        }

                                        return renderable.map((recipe, idx) => (
                                            <div key={`${recipe.id || recipe.name || recipe.title || idx}_${idx}`} className={`${recipe.suggestedPairings?.length > 0 ? 'md:col-span-2 lg:col-span-2' : ''}`}>
                                                <RecipeMenuGroup
                                                    recipe={recipe}
                                                    isDarkMode={isDarkMode}
                                                    onCardClick={onRecipeClick}
                                                    onToggleSelect={toggleSelection}
                                                    selectedRecipeNames={selectedRecipeNames}
                                                    isModern={true}
                                                    userPortion={parseInt(portion)}
                                                />
                                            </div>
                                        ));
                                    })()}
                                </div>
                            )}
                        </div>
                    ) : (
                        loading ? (
                            <div className="flex flex-col items-center justify-center h-80 space-y-8">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-red-500 text-[28px] animate-pulse">kitchen</span>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <p className="font-black text-xl mb-1 text-slate-800 dark:text-white">Menüler Hazırlanıyor</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 italic max-w-xs mx-auto">En çok malzemesi bulunan ve son kullanma tarihi en yakın ürünlere öncelik veriliyor...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-20">
                                {(() => {
                                    const renderedIds = new Set();
                                    const elements = [];

                                    if (structuredMenu) {
                                        const { main, pair, soup, salad, dessert } = structuredMenu;
                                        const orderedItems = [
                                            { id: 'soup', data: soup, label: "İDEAL ÇORBA" },
                                            { id: 'main', data: main, label: (Array.isArray(pair) && pair.length > 0) ? "ANA MENÜ + EŞLİKÇİ" : "ANA MENÜ ÖNERİSİ", pairings: pair },
                                            { id: 'salad', data: salad, label: "İDEAL SALATA" },
                                            { id: 'dessert', data: dessert, label: "İDEAL TATLI" }
                                        ];

                                        const visibleItems = orderedItems.filter(item => selectedCategories.includes(item.id) && item.data);

                                        visibleItems.forEach((item) => {
                                            const recipeWithPairs = item.id === 'main'
                                                ? { ...item.data, suggestedPairings: item.pairings }
                                                : item.data;

                                            // Track what we've rendered in structured menu
                                            renderedIds.add(`${item.data.id}_${item.id === 'main' && item.pairings?.length > 0}`);

                                            elements.push(
                                                <div key={`structured_${item.id}`} className={`${item.id === 'main' && item.pairings?.length > 0 ? 'md:col-span-2 lg:col-span-2' : ''} flex flex-col h-full animate-in fade-in zoom-in duration-500`}>
                                                    <div className="mb-4 px-1">
                                                        <span className="px-4 py-1.5 bg-primary/10 text-primary text-[10px] font-black rounded-xl uppercase tracking-widest border border-primary/20">
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                    <RecipeMenuGroup
                                                        recipe={recipeWithPairs}
                                                        isDarkMode={isDarkMode}
                                                        onCardClick={onRecipeClick}
                                                        onToggleSelect={toggleSelection}
                                                        selectedRecipeNames={selectedRecipeNames}
                                                        isModern={true}
                                                        userPortion={parseInt(portion)}
                                                    />
                                                </div>
                                            );
                                        });

                                        // Add a separator if we have other recipes
                                        if (recipes && recipes.length > 0) {
                                            elements.push(
                                                <div key="separator" className="col-span-full pt-8 pb-2 border-b border-slate-100 dark:border-slate-800 mb-4 px-2">
                                                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Alternatif Öneriler</h3>
                                                </div>
                                            );
                                        }
                                    }

                                    // Render other recipes (solo or different pairings)
                                    if (recipes && recipes.length > 0) {
                                        const filteredRecipes = recipes.filter(r => {
                                            const key = `${r.id}_${r.suggestedPairings?.length > 0}`;
                                            if (renderedIds.has(key)) return false;

                                            // Category check — [SS-01] salata SADECE serveStyle üzerinden
                                            const rawIdx = r.categoryIndex !== undefined ? r.categoryIndex : 1;
                                            const idx = Number(rawIdx);
                                            const style = (r.serveStyle || '').toLowerCase();
                                            if (selectedCategories.includes('soup') && (idx === 0 || style === 'corba' || style === 'soup')) return true;
                                            if (selectedCategories.includes('main') && (idx === 1 || isNaN(idx) || style === 'ana_yemek' || style === 'main')) return true;
                                            if (selectedCategories.includes('salad') && (style === 'salata' || style === 'salad' || style === 'meze')) return true;
                                            if (selectedCategories.includes('dessert') && (idx === 3 || style === 'tatli' || style === 'dessert')) return true;
                                            return false;
                                        });

                                        filteredRecipes.forEach((recipe, idx) => {
                                            elements.push(
                                                <div key={`extra_${idx}`} className={`${recipe.suggestedPairings?.length > 0 ? 'md:col-span-2 lg:col-span-2' : ''} flex flex-col h-full`}>
                                                    <RecipeMenuGroup
                                                        recipe={recipe}
                                                        isDarkMode={isDarkMode}
                                                        onCardClick={onRecipeClick}
                                                        onToggleSelect={toggleSelection}
                                                        selectedRecipeNames={selectedRecipeNames}
                                                        isModern={true}
                                                        userPortion={parseInt(portion)}
                                                    />
                                                </div>
                                            );
                                        });
                                    }

                                    if (elements.length === 0) {
                                        return (
                                            <div className="col-span-full py-24 text-center">
                                                <div className="size-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6">
                                                    <span className="material-symbols-outlined text-[40px] text-slate-400">restaurant_menu</span>
                                                </div>
                                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Henüz uygun tarif yok</h3>
                                                <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">Seçtiğiniz kategorilerde elinizdeki malzemelerle yapılabilecek bir tarif bulamadık.</p>
                                            </div>
                                        );
                                    }

                                    return elements;
                                })()}
                            </div>
                        )
                    )}
                </div>

                {/* Dynamic Bottom Action Bar - Only show when at least one recipe is selected */}
                {!loading && selectedRecipeNames.length > 0 && (
                    <div className="fixed md:absolute bottom-6 md:bottom-10 left-0 right-0 flex justify-center pointer-events-none z-50 px-4">
                        <div className="pointer-events-auto flex items-center gap-2 md:gap-4 bg-white/95 dark:bg-slate-800/95 backdrop-blur-2xl shadow-2xl p-2 md:p-3 px-4 md:px-8 rounded-2xl md:rounded-3xl border border-slate-100 dark:border-slate-700 animate-in slide-in-from-bottom-8 duration-500 w-full max-w-lg mx-auto">
                            <div className="flex flex-col shrink-0 min-w-0">
                                <span className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight">DURUM</span>
                                <span className="text-xs md:text-sm font-black text-green-500 truncate">
                                    {selectedRecipeNames.length} Tarif
                                </span>
                            </div>

                            <div className="w-px h-6 md:h-8 bg-slate-200 dark:bg-slate-700/50 mx-1 md:mx-2" />

                            <button
                                onClick={handleMarkMenuAsUsed}
                                disabled={isApproving}
                                className={`flex-1 flex items-center justify-center gap-2 md:gap-3 px-4 md:px-8 py-2.5 md:py-3.5 rounded-xl md:rounded-2xl font-black text-[11px] md:text-sm transition-all active:scale-95 shadow-xl ${isApproving
                                    ? 'bg-slate-300 dark:bg-slate-700 text-white cursor-not-allowed'
                                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-green-500/30 hover:-translate-y-0.5'
                                    }`}
                            >
                                {isApproving ? (
                                    <div className="w-4 h-4 md:w-5 md:h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <span className="material-symbols-outlined text-[18px] md:text-[24px]">skillet</span>
                                )}
                                <span className="whitespace-nowrap">MENÜYÜ ONAYLA</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Recipe Details Modal (Nested) */}
            {recipeModalOpen && selectedRecipe && (
                <RecipeModalGateway
                    modalVariant="smart"
                    isOpen={recipeModalOpen}
                    onClose={onRecipeModalClose}
                    recipe={selectedRecipe}
                    isDarkMode={isDarkMode}
                    userProfile={userProfile}
                    skillLevel={skillLevel}
                    userPortion={parseInt(portion)}
                    onUsedSuccess={onRecipeUsedSuccess}
                    availableComponents={availableComponents}
                />
            )}
        </div>
    );
}
